import React, { useState } from 'react';
import { Star, Gift, CreditCard, TrendingUp, Check, ArrowRight, Award } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { toast } from 'sonner';
import { memberAPI } from '../services/api';

export const MembershipProgram = () => {
  const [isSignUpOpen, setIsSignUpOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });

  const tiers = [
    {
      name: 'Bronze',
      icon: '🥉',
      color: 'from-amber-700 to-amber-900',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-200',
      textColor: 'text-amber-800',
      pointsRequired: 0,
      benefits: [
        'Earn 1 point per $1 spent',
        '5% discount on all services',
        'Birthday month special offer',
        'Priority booking'
      ]
    },
    {
      name: 'Silver',
      icon: '🥈',
      color: 'from-gray-400 to-gray-600',
      bgColor: 'bg-gray-50',
      borderColor: 'border-gray-300',
      textColor: 'text-gray-800',
      pointsRequired: 500,
      benefits: [
        'Earn 1.5 points per $1 spent',
        '10% discount on all services',
        'Free tire rotation (2x per year)',
        'Birthday month special offer',
        'Priority booking'
      ],
      popular: true
    },
    {
      name: 'Gold',
      icon: '🥇',
      color: 'from-yellow-400 to-yellow-600',
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-300',
      textColor: 'text-yellow-900',
      pointsRequired: 1500,
      benefits: [
        'Earn 2 points per $1 spent',
        '15% discount on all services',
        'Free tire rotation (4x per year)',
        'Free oil change (2x per year)',
        'Birthday month special offer',
        'VIP priority support',
        '24/7 emergency service discount'
      ]
    }
  ];

  const handleSignUp = async (e) => {
    e.preventDefault();
    
    try {
      // Create member via API
      const member = await memberAPI.signup(formData);
      
      // Store member data in localStorage
      localStorage.setItem('memberData', JSON.stringify(member));
      
      toast.success('Welcome to Vargas VIP Club!', {
        description: `Your Bronze membership (#${member.member_number}) is now active. Start earning points today!`
      });
      
      setIsSignUpOpen(false);
      setFormData({ name: '', email: '', phone: '' });
    } catch (error) {
      console.error('Error creating member:', error);
      if (error.response?.status === 400) {
        toast.error('Email already registered', {
          description: 'This email is already a VIP member. Use a different email or login.'
        });
      } else {
        toast.error('Failed to create membership. Please try again.');
      }
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  return (
    <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-900 via-gray-900 to-slate-900 text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-red-600 rounded-full filter blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-yellow-500 rounded-full filter blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-red-600 to-red-700 rounded-2xl mb-6 shadow-2xl">
            <Award className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-4xl lg:text-5xl font-extrabold mb-4">
            Join Vargas VIP Club
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-6">
            Earn points with every purchase, unlock exclusive benefits, and save on every service
          </p>
          <div className="flex items-center justify-center gap-8 text-sm">
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
              <span>Free to Join</span>
            </div>
            <div className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-red-400" />
              <span>Instant Rewards</span>
            </div>
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-400" />
              <span>Build Points Fast</span>
            </div>
          </div>
        </div>

        {/* Membership Tiers */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {tiers.map((tier) => (
            <Card
              key={tier.name}
              className={`relative p-8 bg-white text-gray-900 hover:shadow-2xl transition-all ${
                tier.popular ? 'ring-4 ring-red-500 scale-105' : ''
              }`}
            >
              {tier.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-red-600 text-white px-4 py-1 text-sm font-bold">
                    MOST POPULAR
                  </Badge>
                </div>
              )}
              
              <div className="text-center mb-6">
                <div className="text-6xl mb-3">{tier.icon}</div>
                <h3 className="text-3xl font-bold mb-2">{tier.name}</h3>
                {tier.pointsRequired > 0 && (
                  <p className="text-sm text-gray-600">
                    Unlock at {tier.pointsRequired} points
                  </p>
                )}
              </div>

              <div className="space-y-3 mb-8">
                {tier.benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>

              {tier.name === 'Bronze' && (
                <Dialog open={isSignUpOpen} onOpenChange={setIsSignUpOpen}>
                  <DialogTrigger asChild>
                    <Button className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white shadow-lg">
                      Join Now - Free
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle className="text-2xl font-bold">Join Vargas VIP Club</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSignUp} className="space-y-4 mt-4">
                      <div>
                        <Label htmlFor="member-name">Full Name *</Label>
                        <Input
                          id="member-name"
                          required
                          value={formData.name}
                          onChange={(e) => handleChange('name', e.target.value)}
                          placeholder="John Doe"
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label htmlFor="member-email">Email Address *</Label>
                        <Input
                          id="member-email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => handleChange('email', e.target.value)}
                          placeholder="john@example.com"
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label htmlFor="member-phone">Phone Number *</Label>
                        <Input
                          id="member-phone"
                          type="tel"
                          required
                          value={formData.phone}
                          onChange={(e) => handleChange('phone', e.target.value)}
                          placeholder="(555) 123-4567"
                          className="mt-2"
                        />
                      </div>
                      <Button
                        type="submit"
                        className="w-full bg-red-600 hover:bg-red-700 text-white"
                      >
                        Create My Free Account
                      </Button>
                      <p className="text-xs text-gray-500 text-center">
                        By joining, you agree to receive exclusive offers and updates.
                      </p>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </Card>
          ))}
        </div>

        {/* How It Works */}
        <Card className="p-8 bg-white/5 backdrop-blur-sm border-2 border-white/10">
          <h3 className="text-2xl font-bold text-center mb-8">How It Works</h3>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <CreditCard className="w-8 h-8" />
              </div>
              <h4 className="font-bold mb-2">1. Sign Up Free</h4>
              <p className="text-sm text-gray-300">
                Create your account in 30 seconds
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-red-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-8 h-8" />
              </div>
              <h4 className="font-bold mb-2">2. Earn Points</h4>
              <p className="text-sm text-gray-300">
                Get points with every dollar spent
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-red-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8" />
              </div>
              <h4 className="font-bold mb-2">3. Unlock Tiers</h4>
              <p className="text-sm text-gray-300">
                Advance to Silver and Gold levels
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-red-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Gift className="w-8 h-8" />
              </div>
              <h4 className="font-bold mb-2">4. Get Rewards</h4>
              <p className="text-sm text-gray-300">
                Redeem points for discounts & perks
              </p>
            </div>
          </div>
        </Card>

        {/* Bottom CTA */}
        <div className="text-center mt-12">
          <Dialog open={isSignUpOpen} onOpenChange={setIsSignUpOpen}>
            <DialogTrigger asChild>
              <Button
                size="lg"
                className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white text-lg px-12 py-6 shadow-2xl"
              >
                Start Earning Rewards Today
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </DialogTrigger>
          </Dialog>
          <p className="text-sm text-gray-400 mt-4">
            Already a member? Your points and benefits are automatically applied at checkout
          </p>
        </div>
      </div>
    </section>
  );
};
